import * as THREE from 'three';
import { NEST_DEPTH_DIM } from '../../constants';

const _c1 = new THREE.Vector3();
const _c2 = new THREE.Vector3();

/**
 * Soft bow between two nodes. Short wires barely bend; long (cross-island) ones
 * arc more. Side is stable for a given pair so the graph doesn't shimmer.
 */
export function dependencyCurve(from, to, into) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const bow = Math.min(2.8, 0.12 + len * 0.14);
  const side = ((from.x + to.y) * 13 + (to.x - from.y) * 7) >= 0 ? 1 : -1;
  const px = (-dy / len) * bow * side;
  const py = (dx / len) * bow * side;
  _c1.set((from.x + to.x) * 0.5 + px, (from.y + to.y) * 0.5 + py, (from.z + to.z) * 0.5);
  if (into?.isQuadraticBezierCurve3) {
    into.v0.copy(from);
    into.v1.copy(_c1);
    into.v2.copy(to);
    return into;
  }
  return new THREE.QuadraticBezierCurve3(from.clone(), _c1.clone(), to.clone());
}

/**
 * Hub → nested entry: leave the hub on its own plane, then drop and run out.
 * Dropping from the centre put the first visible sample behind the disk after
 * rim trim, so the stem stopped short of the parent ring.
 */
export function nestStemCurve(from, to, into) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  _c1.set(from.x + dx * 0.2, from.y + dy * 0.2, from.z);
  _c2.set(from.x + dx * 0.28, from.y + dy * 0.28, to.z);
  if (into?.isCubicBezierCurve3) {
    into.v0.copy(from);
    into.v1.copy(_c1);
    into.v2.copy(_c2);
    into.v3.copy(to);
    return into;
  }
  return new THREE.CubicBezierCurve3(from.clone(), _c1.clone(), _c2.clone(), to.clone());
}

/**
 * Nested leaf → hub: run along the nested layer, then rise onto the hub plane
 * before entering the ring. Same sideways bow so a one-task loop stays open.
 */
export function nestReturnCurve(from, to, into) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const bow = 0.45 + len * 0.08;
  const px = (-dy / len) * bow;
  const py = (dx / len) * bow;
  _c1.set(from.x + dx * 0.55 + px, from.y + dy * 0.55 + py, from.z);
  _c2.set(to.x - dx * 0.2 + px * 0.25, to.y - dy * 0.2 + py * 0.25, to.z);
  if (into?.isCubicBezierCurve3) {
    into.v0.copy(from);
    into.v1.copy(_c1);
    into.v2.copy(_c2);
    into.v3.copy(to);
    return into;
  }
  return new THREE.CubicBezierCurve3(from.clone(), _c1.clone(), _c2.clone(), to.clone());
}

/** Pick the wire shape for an edge kind. Reuses `into` when the type matches. */
export function curveForKind(kind, from, to, into) {
  if (kind === 'nest') return nestStemCurve(from, to, into);
  if (kind === 'return') return nestReturnCurve(from, to, into);
  return dependencyCurve(from, to, into);
}

/** Glow / opacity multiplier for a node's nesting level. Root = 1. */
export function nestDepthDim(node) {
  const d = node?.nestDepth ?? 0;
  return d > 0 ? Math.pow(NEST_DEPTH_DIM, d) : 1;
}
